import { Link } from "react-router-dom";
import { channels, type Channel } from "@/mocks/channels";
import { topics, type Topic } from "@/mocks/topics";
import { formatNum } from "@/lib/format";

const rankColor = (i: number) =>
  i === 0 ? "bg-primary-500 text-background-50" : i === 1 ? "bg-primary-200 text-primary-800" : "bg-background-200 text-foreground-600";

/** 热议榜侧栏（复用榜单） */
export default function RankSidebar({
  activeSlug,
  title = "全网热议榜",
}: {
  activeSlug?: string;
  title?: string;
}) {
  const sorted = [...topics].sort((a, b) => b.heat - a.heat).slice(0, 6);
  return (
    <aside className="card-tint rounded-2xl p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-base font-bold text-foreground-900">
          <i className="ri-fire-fill text-primary-500" aria-hidden="true" />
          {title}
        </h3>
        <Link to="/rank" className="text-xs text-foreground-500 hover:text-primary-700">
          查看全部
        </Link>
      </div>
      <ol className="space-y-1">
        {sorted.map((t, i) => (
          <li key={t.slug}>
            <Link
              to={`/topics/${t.slug}`}
              className={`flex items-start gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-background-50 ${
                activeSlug === t.slug ? "bg-background-50" : ""
              }`}
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-xs font-bold ${rankColor(i)}`}
              >
                {i + 1}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800">
                  {t.title}
                </span>
                <span className="mt-1 flex items-center gap-1 text-xs text-foreground-400">
                  <i className="ri-flashlight-fill text-accent-500" aria-hidden="true" />
                  热度 {formatNum(t.heat)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
      <div className="mt-3 border-t border-background-200 pt-3">
        <p className="mb-2 text-xs text-foreground-500">按热度围观</p>
        <div className="flex flex-wrap gap-1.5">
          {channels.map((c: Channel) => (
            <Link
              key={c.slug}
              to={`/channels/${c.slug}`}
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 hover:bg-secondary-200"
            >
              #{c.label}
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
}

/** 相关话题（同频道推荐） */
export function RelatedTopics({ topic }: { topic: Topic }) {
  const related = topics.filter((t) => t.channel === topic.channel && t.slug !== topic.slug).slice(0, 3);
  const fallback = topics.filter((t) => t.slug !== topic.slug).slice(0, 3);
  const list = related.length ? related : fallback;
  return (
    <aside className="card-tint rounded-2xl p-4">
      <h3 className="mb-3 flex items-center gap-1.5 text-base font-bold text-foreground-900">
        <i className="ri-arrow-right-circle-line text-primary-500" aria-hidden="true" />
        相关话题
      </h3>
      <div className="space-y-3">
        {list.map((t) => (
          <Link key={t.slug} to={`/topics/${t.slug}`} className="group flex gap-3">
            <img
              src={t.image}
              alt={t.title}
              loading="lazy"
              className="h-14 w-20 shrink-0 rounded-lg object-cover"
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700">
                {t.title}
              </span>
              <span className="mt-1 text-xs text-foreground-400">
                {formatNum(t.comments)} 条讨论 · {t.updated}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </aside>
  );
}